/**
 * Series wizard: create size products in a group, apply shared variant/package,
 * open BOM sandbox sessions for gap briefs (offer-and-resume).
 */
import { useEffect, useMemo, useState } from "react";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingModelsSeriesWizardForm } from "./ManufacturingModelsSeriesWizardForm";
import { attachPackageToRecipes } from "@/lib/manufacturing/attachPackageToRecipes";
import { createManufacturingBomAuthoringSession } from "@/lib/manufacturing/bomAuthoringSessionsClient";
import {
  applyManufacturingNamedVariantToRecipes,
  listManufacturingNamedVariants,
  type NamedVariantSummary,
} from "@/lib/manufacturing/namedVariantsClient";
import {
  createManufacturingRecipeGroup,
  listManufacturingRecipeGroups,
  type MfgRecipeGroupSummary,
} from "@/lib/manufacturing/recipeGroupsClient";
import { createManufacturingRecipe } from "@/lib/manufacturing/recipesClient";
import {
  buildSeriesSizeProducts,
  parseSeriesGapBriefs,
  resolveSeriesIdentity,
} from "@/lib/manufacturing/seriesWizardPlan";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

type EnsuredGroup = { id: string; code: string; name: string };

export function ManufacturingModelsSeriesWizardModal({
  recipes,
  initialGroupId,
  onClose,
  onDone,
}: {
  recipes: readonly MfgRecipeSummary[];
  initialGroupId?: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [groups, setGroups] = useState<MfgRecipeGroupSummary[]>([]);
  const [groupId, setGroupId] = useState(initialGroupId ?? "");
  const [newGroupCode, setNewGroupCode] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [seriesName, setSeriesName] = useState("");
  const [seriesCode, setSeriesCode] = useState("");
  const [sizesRaw, setSizesRaw] = useState("");
  const [namedVariantId, setNamedVariantId] = useState("");
  const [packageId, setPackageId] = useState("");
  const [gapsRaw, setGapsRaw] = useState("");
  const [variants, setVariants] = useState<NamedVariantSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);

  const packages = useMemo(
    () => recipes.filter((r) => r.kind === "package" && r.is_active),
    [recipes],
  );

  useEffect(() => {
    void (async () => {
      const [g, v] = await Promise.all([
        listManufacturingRecipeGroups(),
        listManufacturingNamedVariants(),
      ]);
      if (g.ok) setGroups(g.data.groups);
      if (v.ok) {
        setVariants(v.data.variants.filter((x) => x.status === "active" || x.status === "draft"));
      }
    })();
  }, []);

  useEffect(() => {
    if (initialGroupId) setGroupId(initialGroupId);
  }, [initialGroupId]);

  useEffect(() => {
    if (!groupId) return;
    const g = groups.find((x) => x.id === groupId);
    if (!g) return;
    if (!seriesName.trim()) setSeriesName(g.name);
    if (!seriesCode.trim()) setSeriesCode(g.code);
  }, [groupId, groups]);

  async function ensureGroup(): Promise<EnsuredGroup | null> {
    if (groupId) {
      const existing = groups.find((x) => x.id === groupId);
      return {
        id: groupId,
        code: existing?.code ?? "",
        name: existing?.name ?? "",
      };
    }
    const code = newGroupCode.trim();
    const name = newGroupName.trim() || seriesName.trim();
    if (!code || !name) {
      setError("Select a group, or enter new group code + name");
      return null;
    }
    const created = await createManufacturingRecipeGroup({ code, name });
    if (!created.ok) {
      setError(created.error);
      return null;
    }
    setGroups((prev) => [...prev, created.data.group]);
    setGroupId(created.data.group.id);
    return {
      id: created.data.group.id,
      code: created.data.group.code,
      name: created.data.group.name,
    };
  }

  async function runWizard() {
    setBusy(true);
    setError(null);
    setLog([]);
    const lines: string[] = [];

    const group = await ensureGroup();
    if (!group) {
      setBusy(false);
      return;
    }

    const identity = resolveSeriesIdentity({
      seriesName,
      seriesCode,
      newGroupName,
      newGroupCode,
      groupName: group.name,
      groupCode: group.code,
    });
    if (!identity.ok) {
      setBusy(false);
      setError(identity.error);
      return;
    }

    const plan = buildSeriesSizeProducts({
      seriesName: identity.seriesName,
      seriesCode: identity.seriesCode,
      sizesRaw,
      recipe_group_id: group.id,
    });
    if (!plan.ok) {
      setBusy(false);
      setError(plan.error);
      return;
    }

    const createdIds: string[] = [];
    const gid = group.id;
    for (const product of plan.products) {
      const existing = recipes.find(
        (r) =>
          r.recipe_group_id === gid &&
          (r.code.toLowerCase() === product.code.toLowerCase() ||
            (r.kind === "product" &&
              r.tags.some((t) => t.toLowerCase() === `size:${product.sizeLabel}`.toLowerCase()))),
      );
      if (existing) {
        createdIds.push(existing.id);
        lines.push(`Linked existing ${existing.code}`);
        continue;
      }
      const created = await createManufacturingRecipe(product);
      if (!created.ok) {
        setBusy(false);
        setError(created.error);
        setLog(lines);
        return;
      }
      createdIds.push(created.data.recipe.id);
      lines.push(`Created ${created.data.recipe.code}`);
    }

    if (namedVariantId && createdIds.length > 0) {
      const applied = await applyManufacturingNamedVariantToRecipes({
        named_variant_id: namedVariantId,
        parent_recipe_ids: createdIds,
        description: `Series shared variant for ${identity.seriesName}`,
      });
      if (!applied.ok) {
        setBusy(false);
        setError(applied.error);
        setLog(lines);
        return;
      }
      lines.push(`Applied named variant to ${applied.data.applications.length} products`);
    }

    if (packageId && createdIds.length > 0) {
      const attached = await attachPackageToRecipes(createdIds, packageId);
      const failed = attached.find((a) => !a.ok);
      if (failed && !failed.ok) {
        setBusy(false);
        setError(failed.error);
        setLog(lines);
        return;
      }
      const n = attached.filter((a) => a.ok && !a.skipped).length;
      lines.push(`Attached package to ${n} products`);
    }

    const gaps = parseSeriesGapBriefs(gapsRaw);
    let gapSessions = 0;
    for (const gap of gaps) {
      const session = await createManufacturingBomAuthoringSession({
        brief_text: gap.brief_text,
        source: "typed",
        recipe_group_id: gid,
      });
      if (!session.ok) {
        lines.push(`Gap “${gap.label}” failed: ${session.error}`);
        continue;
      }
      gapSessions += 1;
      lines.push(`Opened sandbox session for “${gap.label}” (${session.data.session.status})`);
    }

    setBusy(false);
    setLog(lines);
    const summary = [
      `${createdIds.length} size product${createdIds.length === 1 ? "" : "s"}`,
      gapSessions > 0 ? `${gapSessions} gap session${gapSessions === 1 ? "" : "s"}` : null,
    ]
      .filter(Boolean)
      .join(", ");
    onDone(`Series wizard done — ${summary}. Resume gaps via Start from brief.`);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">
              Series wizard
            </h2>
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
              Create size products in a group, apply shared pieces, open sandbox sessions for gaps.
            </p>
          </div>
          <button type="button" className={ghostBtnClass} onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {error}
            </div>
          ) : null}

          <ManufacturingModelsSeriesWizardForm
            groups={groups}
            groupId={groupId}
            onGroupIdChange={setGroupId}
            newGroupCode={newGroupCode}
            onNewGroupCodeChange={setNewGroupCode}
            newGroupName={newGroupName}
            onNewGroupNameChange={setNewGroupName}
            seriesName={seriesName}
            onSeriesNameChange={setSeriesName}
            seriesCode={seriesCode}
            onSeriesCodeChange={setSeriesCode}
            sizesRaw={sizesRaw}
            onSizesRawChange={setSizesRaw}
            namedVariantId={namedVariantId}
            onNamedVariantIdChange={setNamedVariantId}
            packageId={packageId}
            onPackageIdChange={setPackageId}
            gapsRaw={gapsRaw}
            onGapsRawChange={setGapsRaw}
            variants={variants}
            packages={packages}
          />

          {log.length > 0 ? (
            <ul className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              {log.map((line, i) => (
                <li key={`${i}-${line.slice(0, 24)}`} className="py-0.5">
                  {line}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
          <button type="button" className={ghostBtnClass} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={busy}
            onClick={() => void runWizard()}
          >
            {busy ? "Running…" : "Create series"}
          </button>
        </div>
      </div>
    </div>
  );
}
